if (!customElements.get('collection-quick-add')) {
  customElements.define('collection-quick-add', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.dialog = this.querySelector('dialog');
      this.content = this.querySelector('.collection-quick-add__content');
      this.status = this.querySelector('.collection-quick-add__status');
      this.section = this.closest('[data-section-type="collection"]');

      // The filter component replaces product cards, so delegate from this persistent section.
      this.section.addEventListener('click', (event) => {
        const link = event.target.closest('[data-collection-quick-add]');
        if (!link || !this.section.contains(link) || !this.dialog.showModal) return;
        event.preventDefault();
        this.open(link);
      });
      this.dialog.addEventListener('click', (event) => {
        if (event.target.closest('[data-close]')) this.dialog.close();
        const option = event.target.closest('[data-option-value]');
        if (option && !option.disabled && !this.pending) this.selectOption(option);
      });
      this.dialog.addEventListener('close', () => {
        this.request?.abort();
        document.documentElement.classList.remove('collection-quick-add-open');
        if (this.opener?.isConnected) this.opener.focus();
      });
    }

    async open(link) {
      this.opener = link;
      this.request?.abort();
      this.request = new AbortController();
      const { signal } = this.request;
      this.pending = false;
      this.content.replaceChildren();
      this.status.textContent = 'Loading product…';
      if (!this.dialog.open) this.dialog.showModal();
      document.documentElement.classList.add('collection-quick-add-open');

      try {
        const url = new URL(link.dataset.productUrl, document.baseURI);
        url.searchParams.set('section_id', 'collection-quick-add-product');
        const previewTheme = new URL(window.location.href).searchParams.get('preview_theme_id');
        if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
        const response = await fetch(url, { signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Section request failed');
        const html = await response.text();
        if (signal.aborted) return;
        const parsed = document.createElement('template');
        parsed.innerHTML = html;
        const product = parsed.content.querySelector('[data-quick-add-product]');
        if (!product) throw new Error('Product response was empty');
        this.variants = JSON.parse(product.querySelector('[data-variant-data]').textContent);
        this.content.replaceChildren(product);
        this.form = product.querySelector('.collection-quick-add__form');
        this.groups = Array.from(product.querySelectorAll('[data-option-index]'));
        const wanted = String(link.dataset.variantId || this.form?.dataset.initialVariant);
        this.variant = this.variants.find((variant) => String(variant.id) === wanted)
          || this.variants.find((variant) => variant.available) || this.variants[0];
        product.querySelectorAll('[data-size-guide]').forEach((guide) => {
          if (this.dataset.sizeGuide) {
            guide.href = this.dataset.sizeGuide;
            guide.hidden = false;
          }
        });
        this.updateVariant();
        this.watchCartForm();
        this.status.textContent = '';
      } catch (error) {
        if (signal.aborted) return;
        this.status.textContent = 'This product could not be loaded. Please try again or view the product page.';
        const fallback = document.createElement('a');
        fallback.href = link.href;
        fallback.textContent = 'View product';
        this.content.replaceChildren(fallback);
      }
    }

    watchCartForm() {
      this.cartObserver?.disconnect();
      if (!this.form) return;
      const submit = this.form.querySelector('[type="submit"]');
      this.form.addEventListener('click', (event) => {
        if (event.target.closest('[type="submit"]')) this.pending = true;
      });
      this.cartObserver = new MutationObserver(() => {
        this.form.querySelectorAll('.ajax-add-notice').forEach((notice) => {
          notice.setAttribute('role', notice.classList.contains('ajax-add-notice--error') ? 'alert' : 'status');
        });
        if (this.pending && !submit.disabled) {
          this.pending = false;
          this.updateVariant();
        }
      });
      this.cartObserver.observe(this.form, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'class'] });
    }

    optionCandidates(index, value) {
      const finish = this.groups.find((group) => /^(material|finish|color|colour)$/i.test(group.querySelector('legend').textContent.trim().replace(/:$/, '')));
      const pivot = Number((finish || this.groups[0])?.dataset.optionIndex || 0);
      const order = [pivot, ...this.groups.map((group) => Number(group.dataset.optionIndex)).filter((i) => i !== pivot)];
      const earlier = order.slice(0, order.indexOf(index));
      return this.variants.filter((variant) => variant.available && variant.options[index] === value
        && earlier.every((i) => variant.options[i] === this.variant.options[i]));
    }

    selectOption(button) {
      const index = Number(button.closest('[data-option-index]').dataset.optionIndex);
      const candidates = this.optionCandidates(index, button.dataset.optionValue);
      const previous = this.variant.options;
      candidates.sort((a, b) => b.options.reduce((count, value, i) => count + (value === previous[i]), 0)
        - a.options.reduce((count, value, i) => count + (value === previous[i]), 0));
      if (!candidates[0]) return;
      this.variant = candidates[0];
      this.form?.querySelectorAll('.ajax-add-notice').forEach((notice) => notice.remove());
      this.updateVariant();
    }

    updateVariant() {
      if (!this.variant) return;
      this.groups.forEach((group) => {
        const index = Number(group.dataset.optionIndex);
        group.querySelectorAll('[data-option-value]').forEach((button) => {
          button.disabled = !this.optionCandidates(index, button.dataset.optionValue).length;
          button.setAttribute('aria-pressed', String(this.variant.options[index] === button.dataset.optionValue));
        });
      });
      const template = this.content.querySelector(`template[data-variant-id="${this.variant.id}"]`);
      if (template) {
        const fragment = template.content.cloneNode(true);
        this.content.querySelector('[data-product-price]').replaceChildren(fragment.querySelector('[data-variant-price]'));
        const image = fragment.querySelector('img');
        if (image) this.content.querySelector('[data-product-image]').replaceChildren(image);
      }
      const productLink = this.content.querySelector('[data-product-link]');
      const url = new URL(productLink.href, document.baseURI);
      url.searchParams.set('variant', this.variant.id);
      productLink.href = url.href;
      if (!this.form) return;
      this.form.elements.id.value = this.variant.id;
      this.form.elements.quantity.value = this.variant.quantity;
      const submit = this.form.querySelector('[type="submit"]');
      submit.disabled = !this.variant.available;
      submit.textContent = this.variant.available ? submit.dataset.addLabel : submit.dataset.soldOutLabel;
    }
  });
}
